import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getToken, setToken } from "@/lib/api";
import CONSOLE_DICT from "@/i18n/console";
import { setTranslation } from "@/i18n/translate";

const AuthCtx = createContext(null);
export const useAdmin = () => useContext(AuthCtx);

// Console language: the signed-in staff member's saved language (admin.language), else the last one used on this browser.
const CONSOLE_LANG_KEY = "cw_console_lang";
const savedConsoleLang = () => { try { return localStorage.getItem(CONSOLE_LANG_KEY) || "en"; } catch { return "en"; } };
export function useConsoleLanguage() {
  const { admin } = useAdmin();
  const lang = admin?.language || savedConsoleLang();
  useEffect(() => {
    setTranslation(lang, CONSOLE_DICT);
    try { localStorage.setItem(CONSOLE_LANG_KEY, lang); } catch { /* private mode */ }
  }, [lang]);
  return lang;
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({ admin: null, permissions: null, ready: !getToken() });

  const refresh = useCallback(async () => {
    if (!getToken()) return setState({ admin: null, permissions: null, ready: true });
    try {
      const me = await api("/admin/auth/me");
      setState({ admin: me.admin, permissions: me.permissions, ready: true });
    } catch {
      setState({ admin: null, permissions: null, ready: true });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onUnauth = (e) => {
      setState({ admin: null, permissions: null, ready: true });
      toast.message("Your session ended. Please sign in again.", { description: e.detail });
    };
    window.addEventListener("cw:unauthorized", onUnauth);
    return () => window.removeEventListener("cw:unauthorized", onUnauth);
  }, []);

  const signIn = useCallback(async (token) => {
    setToken(token);
    setState((s) => ({ ...s, ready: false }));
    await refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    setToken(null);
    setState({ admin: null, permissions: null, ready: true });
  }, []);

  // Saves the staff member's own language; the console switches right away.
  const setLanguage = useCallback(async (language) => {
    const res = await api("/admin/auth/language", { method: "PUT", body: { language } });
    setState((s) => ({ ...s, admin: { ...s.admin, ...(res?.admin || {}), language: res?.admin?.language || res?.language || language } }));
  }, []);

  return <AuthCtx.Provider value={{ ...state, signIn, signOut, setLanguage }}>{children}</AuthCtx.Provider>;
}
